/**
 * inside-api 공통 Sync — 유저×serviceName(`dreamity`)당 JSON 스냅샷 1행
 */
import apiClient from '@/services/api/client'
import { SERVICE_NAME } from '@/config/api'
import { encryptSyncJson, decryptSyncJson } from '@/services/dreamitySyncCrypto'
import {
  getSnapshotUserIdFromToken,
  readLocalDreamitySnapshot,
  writeLocalDreamitySnapshot,
  replaceLocalDreamitySnapshotFromServer,
} from '@/services/dreamityLocalSnapshot'
import { normalizePlannerSnapshot, type DreamitySnapshot } from '@/types/dreamitySnapshot.types'

interface PushReqDto {
  serviceName: string
  jsonData: string
}

interface PullResDto {
  jsonData: string
  updatedAt: string
}

interface TimestampResDto {
  updatedAt: string
}

function toMillis(ts: string | undefined): number {
  if (!ts) return 0
  const n = new Date(ts).getTime()
  return Number.isNaN(n) ? 0 : n
}

function toIsoString(v: unknown): string | undefined {
  if (v == null) return undefined
  if (typeof v === 'string') return v
  return String(v)
}

const PUSH_DEBOUNCE_MS = 10_000
const PUSH_MAX_WAIT_MS = 60_000

let pushDebounceTimer: ReturnType<typeof setTimeout> | null = null
let pushMaxWaitTimer: ReturnType<typeof setTimeout> | null = null
let pushBurstStartedAt: number | null = null
let scheduledPushInFlight = false

function clearScheduledPushTimers(): void {
  if (pushDebounceTimer) {
    clearTimeout(pushDebounceTimer)
    pushDebounceTimer = null
  }
  if (pushMaxWaitTimer) {
    clearTimeout(pushMaxWaitTimer)
    pushMaxWaitTimer = null
  }
}

function armScheduledDreamityPushTimers(): void {
  const now = Date.now()
  if (pushBurstStartedAt == null) pushBurstStartedAt = now

  if (pushDebounceTimer) clearTimeout(pushDebounceTimer)
  pushDebounceTimer = setTimeout(() => {
    pushDebounceTimer = null
    void executeScheduledDreamityPush()
  }, PUSH_DEBOUNCE_MS)

  if (!pushMaxWaitTimer) {
    const remaining = Math.max(0, PUSH_MAX_WAIT_MS - (now - pushBurstStartedAt))
    pushMaxWaitTimer = setTimeout(() => {
      pushMaxWaitTimer = null
      void executeScheduledDreamityPush()
    }, remaining)
  }
}

async function executeScheduledDreamityPush(): Promise<void> {
  if (scheduledPushInFlight) return
  scheduledPushInFlight = true
  try {
    clearScheduledPushTimers()
    await pushDreamitySnapshot()
    const userId = getSnapshotUserIdFromToken()
    const snap = userId ? readLocalDreamitySnapshot(userId) : null
    if (snap?.meta.dirty) {
      pushBurstStartedAt = Date.now()
      armScheduledDreamityPushTimers()
    } else {
      pushBurstStartedAt = null
    }
  } finally {
    scheduledPushInFlight = false
  }
}

export function scheduleDebouncedPushDreamitySnapshot(): void {
  armScheduledDreamityPushTimers()
}

export async function pushDreamitySnapshot(options?: { force?: boolean }): Promise<void> {
  const userId = getSnapshotUserIdFromToken()
  if (!userId) return
  const snap = readLocalDreamitySnapshot(userId)
  if (!snap) return
  if (!snap.meta.dirty && !options?.force) return
  try {
    const plain = JSON.stringify(snap)
    const jsonData = await encryptSyncJson(plain)
    const payload: PushReqDto = { serviceName: SERVICE_NAME, jsonData }
    await apiClient.post('/common/sync/push', payload)
    const next: DreamitySnapshot = {
      ...snap,
      meta: {
        ...snap.meta,
        dirty: false,
        lastKnownServerUpdatedAt: new Date().toISOString(),
      },
    }
    writeLocalDreamitySnapshot(userId, next)
  } catch (err) {
    console.error('[DreamitySync] push failed:', err)
  }
}

let pullInFlight: Promise<void> | null = null
let syncInFlight: Promise<void> | null = null

export async function pullDreamitySnapshot(): Promise<void> {
  if (pullInFlight) return pullInFlight
  
  const run = async () => {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) return

    const local = readLocalDreamitySnapshot(userId)
    const localServerTs = local?.meta.lastKnownServerUpdatedAt

    try {
      const tsRes = await apiClient.get<TimestampResDto>('/common/sync/timestamp', {
        params: { serviceName: SERVICE_NAME },
        validateStatus: (s) => (s >= 200 && s < 300) || s === 204,
      })
      if (tsRes.status === 204) {
        // 서버가 비어있음. 로컬에 데이터가 있다면 서버로 푸시하여 초기화 시도
        if (local && (local.domains.dreams?.length ?? 0) > 0) {
          console.info('[DreamitySync] 서버가 비어있어 로컬 데이터로 초기화를 시도합니다.');
          void pushDreamitySnapshot({ force: true });
        }
        return
      }
      const serverTs = toIsoString(tsRes.data?.updatedAt)
      if (!serverTs || toMillis(serverTs) <= toMillis(localServerTs)) return
    } catch (err) {
      console.warn('[DreamitySync] timestamp failed:', err)
      return
    }

    try {
      const pullRes = await apiClient.get<PullResDto>('/common/sync/pull', {
        params: { serviceName: SERVICE_NAME },
        validateStatus: (s) => (s >= 200 && s < 300) || s === 204,
      })
      if (pullRes.status === 204 || !pullRes.data?.jsonData) return

      const decrypted = await decryptSyncJson(pullRes.data.jsonData)
      const parsed = JSON.parse(decrypted) as unknown
      const normalized = normalizePlannerSnapshot(parsed)
      if (!normalized) {
        console.warn('[DreamitySync] pull: unknown snapshot shape, skip')
        return
      }
      const updatedAt = toIsoString(pullRes.data.updatedAt) ?? new Date().toISOString()
      replaceLocalDreamitySnapshotFromServer(userId, normalized, updatedAt)
    } catch (err) {
      console.error('[DreamitySync] pull failed:', err)
    }
  }

  pullInFlight = run().finally(() => { pullInFlight = null })
  return pullInFlight
}

/** 로그인 직후·탭 복귀: 서버가 더 최신이면 로컬 스냅샷만 갱신 (UI는 계속 REST 기준) */
export async function pullDreamitySnapshotIfNewer(): Promise<void> {
  await pullDreamitySnapshot()
}

/** pull 후 로컬에 남은 dirty면 서버로 백업 */
export async function syncDreamitySnapshotPullThenMaybePush(): Promise<void> {
  if (syncInFlight) return syncInFlight

  const run = async () => {
    await pullDreamitySnapshot()
    await pushDreamitySnapshot()
  }

  syncInFlight = run().finally(() => { syncInFlight = null })
  return syncInFlight
}
